import { createContext, useContext, useEffect, useMemo, useState } from "react";

const KEY = "roadrnr_theme"; // 'system' | 'light' | 'dark'
const ThemeContext = createContext(null);

function systemPrefersDark() {
  return typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function readPref() {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "system";
  } catch {
    return "system";
  }
}

function applyResolved(resolved) {
  const root = document.documentElement;
  if (resolved === "light") {
    root.classList.add("light");
    root.classList.remove("dark");
    root.style.colorScheme = "light";
  } else {
    root.classList.add("dark");
    root.classList.remove("light");
    root.style.colorScheme = "dark";
  }
}

export function ThemeProvider({ children }) {
  const [pref, setPref] = useState(readPref);
  const [resolved, setResolved] = useState(() =>
    readPref() === "system" ? (systemPrefersDark() ? "dark" : "light") : readPref()
  );

  useEffect(() => {
    const compute = () =>
      pref === "system" ? (systemPrefersDark() ? "dark" : "light") : pref;
    const r = compute();
    setResolved(r);
    applyResolved(r);

    if (pref === "system" && window.matchMedia) {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const onChange = () => {
        const nr = systemPrefersDark() ? "dark" : "light";
        setResolved(nr);
        applyResolved(nr);
      };
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
  }, [pref]);

  const setTheme = (next) => {
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* ignore */
    }
    setPref(next);
  };

  const value = useMemo(() => ({ theme: pref, resolved, setTheme }), [pref, resolved]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) return { theme: "system", resolved: "dark", setTheme: () => {} };
  return ctx;
}
